import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, useTheme } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
  SlideAnimationProvider,
} from "contexts/SlideAnimation";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { OnboardAction, SearchParams, VAStatus } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import { fetcherAuth } from "utils/api";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { useTracking } from "utils/useTracking";

import AboutForm from "./AboutForm";
import { getUserType } from "./AboutForm/helpers";
import CategoryForm from "./CategoryForm";
import CongratsComplete from "./CongratsComplete";
import FinalOptInForm from "./FinalOptInForm";
import IncomeForm from "./IncomeForm";
import InstagramOnboardingSteps from "./InstagramOnboardingSteps";
import WelcomePage from "./WelcomePage";
import { getOnboardingPage, onboardingNavigate } from "./helper";
import styles from "./styles";

interface SaveOnboardingOptions {
  setOnboardingForm?: boolean;
  showButtonLoading?: boolean;
}

const OnboardingInfluencer = () => {
  const {
    currentOrg,
    currentUser,
    setCurrentUser,
    setCurrentOrg,
    setOrganizationVaProfile,
    setUserNotifications,
    setUserIntegrations,
    setEmailSettings,
    setHideCityDialog,
    setProfile,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
  } = useContext(OrganizationUserContext);
  const { editTemplateDialogId, setEditTemplateDialogId } = useContext(
    OutreachTemplatesContext,
  );
  const theme = useTheme();
  const { setSubscription } = useContext(SubscriptionContext);
  const [searchParams] = useSearchParams();

  const { slideOutUp, slideInUp, slideOutDown, slideInDown, slideProps } =
    useContext(SlideAnimationContext);
  const { trackUserHasOnboarded } = useTracking();

  const referralCode = searchParams.get(SearchParams.REFERRAL_CODE);
  const affiliateCode = searchParams.get(SearchParams.AFFILIATE_CODE);

  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken, signOut } = useAuth();

  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const source = getSource();

  const [onboardingForm, setOnboardingForm] = useState<Map>({
    name: currentUser?.name || "",
    location: "",
    brands: [],
    wishlistBrands: [],
    isUgcCreator: null,
    isInfluencer: null,
    incomeLevel: "",
    pastBrandValidation: null,
    primarySocialMediaPlatform: "instagram",
    defaultTemplateHighlights: [],

    // These fields are auto-filled when user sign up
    email: "",
    organizations: [],
    organizationId: "",
    organizationName: "",
    type: "influencer",
  });

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/user?affiliate_code=${affiliateCode || ""}`,
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setOrganizationVaProfile(res.organizationVaProfile);
      setSubscription(res.subscription);
      setUserIntegrations(res.userIntegrations);
      setUserNotifications(res.userNotifications);
      setEmailSettings(res.emailSetting);
      setIncludedBentoSearchFilterIds(res.includedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(res.excludedBentoSearchFilterIds);
      setProfile(res.organizationProfile);
      setFetchUserLoading(false);
      if (
        res.organization.vaStatus === VAStatus.pending &&
        res.organization.isVaUser
      ) {
        navigate(`/${routes.onboardingVaSelfServe}${location.search}`);
        return;
      }

      // Edge case: user who subscribed to monthly VA and click on the Connect to Google link
      const openIntegrationDialog =
        searchParams.get("open_integration_dialog") === "true";

      // Edge case: user who subscribed to monthly VA and click on Import from Gmail link.
      const openImportGmail =
        searchParams.get("ref") === "va_subscribed_monthly";

      const upgradeToVaFromBannerLink = searchParams.get("va_banner_clicked");

      if (openImportGmail && !openIntegrationDialog) {
        navigate(`/${routes.trackingView}${location.search}`);
        return;
      }

      if (openIntegrationDialog || upgradeToVaFromBannerLink) {
        navigate(`/${routes.home}/all${location.search}`);
        return;
      }

      setHideCityDialog(true);
      if (res.organizationProfile) {
        setOnboardingForm((prev) => ({
          ...prev,
          ...res.organizationProfile,
          name: res.organizationUser.name,
          pitchEmail: res.organizationProfile.pitchEmail,
          userType: getUserType(res.organizationProfile),
        }));
      } else {
        setOnboardingForm((prev) => ({
          ...prev,
          name: res.organizationUser.name,
        }));
      }

      slideInUp();

      if (!res?.organizationProfile) {
        navigate(`/${routes.welcomeOnboarding}${location.search}`);
        return;
      }
    } catch (error) {
      handleClerkUserSignUp();
    }
  };

  const handleClerkUserSignUp = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/signup`,
        "POST",
        {},
        {
          referralCode,
          affiliateCode,
          source: referralCode ? "referral" : source,
        },
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setOnboardingForm((prev) => ({
        ...prev,
        name: res.organizationUser.name,
      }));

      setHideCityDialog(true);
      navigate(`/${routes.welcomeOnboarding}${location.search}`);
      slideInUp();
      if (
        // @ts-ignore
        typeof window.fbq !== "undefined" &&
        // @ts-ignore
        typeof window.fbq === "function"
      ) {
        // @ts-ignore
        window.fbq("track", "CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.ttq !== "undefined" &&
        // @ts-ignore
        typeof window.ttq.track === "function"
      ) {
        // @ts-ignore
        window.ttq.track("CompleteRegistration");
      }
    } catch (error) {
      signOut();
      navigate(`/${routes.signIn}`);
      setErrorAlert(error);
    } finally {
      setFetchUserLoading(false);
    }
  };

  const handleBack = async () => {
    await slideOutDown();
    slideInDown();

    trackEvent("Onboarding Form - Back Button Pressed", {
      page: getOnboardingPage(location.pathname),
      route: location.pathname,
    });

    navigate(
      `/${onboardingNavigate(location.pathname, onboardingForm, "back")}${
        location.search
      }`,
    );
  };

  const handleSkip = async () => {
    await slideOutUp();
    slideInUp();

    trackEvent("Onboarding Form - Skip Button Pressed", {
      page: getOnboardingPage(location.pathname),
      route: location.pathname,
    });

    if (location.pathname.endsWith(routes.aboutOnboarding)) {
      // save the name if they skip
      saveOnboardingForm({ name: currentUser?.name });
    }

    if (location.pathname.endsWith(routes.instagramSteps)) {
      trackUserHasOnboarded(OnboardAction.FINAL_OPT_IN);
    }
    navigate(
      `/${onboardingNavigate(location.pathname, onboardingForm, "next")}${
        location.search
      }`,
    );
  };

  const handleNext = async (updatedForm?: Map) => {
    if (updatedForm) {
      setOnboardingForm(updatedForm);
    }
    trackEvent("Onboarding Form - Next Button Pressed", {
      page: getOnboardingPage(location.pathname),
      route: location.pathname,
    });

    if (location.pathname.endsWith(routes.instagramSteps)) {
      navigate(`/${routes.home}`);
      return;
    }
    saveOnboardingForm(updatedForm || onboardingForm);
  };

  const moveNext = async () => {
    await slideOutUp();
    navigate(
      `/${onboardingNavigate(location.pathname, onboardingForm, "next")}${
        location.search
      }`,
    );
    slideInUp();
  };

  const saveOnboardingForm = async (
    onboardingForm: Map,
    options?: SaveOnboardingOptions,
  ) => {
    const previousRoute = location.pathname;

    const onboardingFormReq = {
      ...onboardingForm,
      signature: null, // do not overwrite default signature so that it updates using social links
      source: referralCode ? "referral" : source,
      page: previousRoute,
    };
    await moveNext();

    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        onboardingFormReq,
      );

      setProfile({ ...organizationProfile });

      if (options?.setOnboardingForm) {
        setOnboardingForm((prev) => ({ ...prev, ...organizationProfile }));
      }

      if (options?.showButtonLoading) {
        await moveNext();
      }
    } catch (error) {
      if (!options?.showButtonLoading) {
        await slideOutDown();
        slideInDown();
        navigate(`${previousRoute}${location.search}`);
      }
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    }
  };

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <>
      <Grid container sx={styles.root}>
        <Slide
          in={slideProps.in}
          direction={slideProps.direction}
          easing={{
            enter: theme.transitions.easing.easeInOut,
            exit: theme.transitions.easing.easeIn,
          }}
          timeout={{
            enter: ENTER_DELAY,
            exit: EXIT_DELAY,
          }}
        >
          <Grid sx={styles.formContainer} item xs={12}>
            <Routes>
              <Route
                path="/welcome"
                element={<WelcomePage handleNext={handleNext} />}
              />
              <Route
                path="/about"
                element={
                  <AboutForm
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    handleNext={handleNext}
                    handleSkip={handleSkip}
                    hideDiscoveryQuestion={Boolean(referralCode)}
                    handleBack={handleBack}
                  />
                }
              />
              <Route
                path="/category"
                element={
                  <CategoryForm
                    onboardingForm={onboardingForm}
                    handleBack={handleBack}
                    handleSkip={handleSkip}
                    moveNext={() => {
                      trackEvent("Onboarding Form - Next Button Pressed", {
                        page: getOnboardingPage(location.pathname),
                        route: location.pathname,
                      });

                      moveNext();
                    }}
                  />
                }
              />

              <Route
                path="/income"
                element={
                  <IncomeForm
                    handleSkip={handleSkip}
                    handleNext={handleNext}
                    setOnboardingForm={setOnboardingForm}
                    onboardingForm={onboardingForm}
                    handleBack={handleBack}
                  />
                }
              />
              <Route
                path="/opt-in"
                element={
                  <FinalOptInForm
                    handleSkip={handleSkip}
                    handleNext={handleNext}
                    handleBack={handleBack}
                  />
                }
              />
              <Route
                path="/insta"
                element={
                  <InstagramOnboardingSteps
                    handleSkip={handleSkip}
                    handleNext={handleNext}
                    handleBack={handleBack}
                  />
                }
              />
              <Route path="/complete" element={<CongratsComplete />} />
              <Route
                path="*"
                element={
                  <Navigate to={`/${routes.home}${location.search}`} replace />
                }
              />
            </Routes>
          </Grid>
        </Slide>
      </Grid>
      {editTemplateDialogId && (
        <EditTemplateDialog
          propsTemplateId={editTemplateDialogId}
          propsHandleClose={() => {
            setEditTemplateDialogId(null);
          }}
        />
      )}
    </>
  );
};

export default function Onboarding() {
  return (
    <SlideAnimationProvider>
      <OnboardingInfluencer />
    </SlideAnimationProvider>
  );
}
